

















































import { computed, defineComponent, ref, toRef, watch } from "@vue/composition-api";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import { InputMassMessage, MassMessage } from "@scrile/api-provider/dist/api/MassMessageProvider";
import providers from "@/lib/api";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { ERRORS } from "@/const";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BasePage from "@/components/BasePage";
import GroupListSelection from "@/components/GroupListSelection";
import PageTitle from "@/components/PageTitle";
import RestrictedWordConfirm from "@/components/RestrictedWordConfirm";
import { extractRestrictedWords } from "@/components/RestrictedWordConfirm/helpers";
import MassMessageConfirm from "./components/MassMessageConfirm.vue";
import MassMessageEditView from "./components/MassMessageEditView.vue";

export default defineComponent({
  name: "MassMessageEdit",
  props: {
    groupList: { type: Array as () => Group[], required: true },
    massMessage: { type: Object as () => MassMessage },
    groupIds: { type: Array as () => string[] },
    defaultBack: { type: Object },
    selectedGroupIds: { type: Array as () => string[] },
  },
  components: {
    RestrictedWordConfirm,
    BaseIcon,
    BaseIconButton,
    BasePage,
    PageTitle,
    MassMessageConfirm,
    MassMessageEditView,
    GroupListSelection,
  },
  setup(props, { emit }) {
    const groupIdsLocal = ref<string[]>(props.selectedGroupIds ?? []);
    const excludedGroupIdsLocal = ref<string[]>([]);
    const recipientCount = ref(0);
    const isGroupMode = ref(false);
    const isShowCancelConfirm = ref(false);
    const resetGroupsSelection = ref<() => void>();
    const loading = ref(false);
    const showRestrictedWordConfirm = ref(false);
    const restrictedWords = ref<string[]>([]);

    const isEditMode = computed(() => !!props.massMessage?.id);
    const vOnTitle = computed(() => {
      if (!isGroupMode.value && !props.massMessage) {
        return undefined;
      }
      if (isGroupMode.value && groupIdsLocal.value.length === 0 && !props.massMessage) {
        return undefined;
      }

      return {
        click: () => {
          if (!isGroupMode.value && props.massMessage) {
            emit("back");
          } else {
            isGroupMode.value = false;
            resetGroupsSelection.value && resetGroupsSelection.value();
          }
        },
      };
    });
    const title = computed(() => {
      if (isGroupMode.value) return $t("Select audience");
      if (isEditMode.value) return $t("Edit");
      return $t("Create a mass message");
    });
    const onSubmit = async (massMessage: InputMassMessage) => {
      try {
        loading.value = true;
        providers.MassMessageProvider.setReceiveErrors(false);
        const updatedMassMessage = await providers.MassMessageProvider.update({ data: massMessage });
        emit("save", updatedMassMessage);
      } catch (e) {
        handleRequestError(e, [
          {
            comparator: e => e.details?.messageTemplate === ERRORS.RESTRICTED_WORD,
            onSuccessCB: e => {
              showRestrictedWordConfirm.value = true;
              restrictedWords.value = extractRestrictedWords(e);
            },
          },
        ]);
      } finally {
        loading.value = false;
      }
    };
    const onConfirm = async () => {
      if (!props.massMessage) return;
      try {
        loading.value = true;
        const result = await providers.MassMessageProvider.cancel({ id: props.massMessage.id });
        result && emit("delete", props.massMessage.id);
      } finally {
        isShowCancelConfirm.value = false;
        loading.value = false;
      }
    };
    const onContinue = async ({
      groupIds,
      excludedGroupIds,
      recipientCount: recipientCountNew,
    }: {
      groupIds: string[];
      excludedGroupIds: string[];
      recipientCount: number;
    }) => {
      recipientCount.value = recipientCountNew;
      groupIdsLocal.value = groupIds;
      excludedGroupIdsLocal.value = excludedGroupIds;
      isGroupMode.value = false;
    };

    watch(
      recipientCount,
      () => {
        if (
          recipientCount.value === 0 &&
          !props.massMessage &&
          (!props.selectedGroupIds || props.selectedGroupIds.length === 0)
        ) {
          isGroupMode.value = true;
        }
      },
      { immediate: true }
    );

    watch(
      toRef(props, "massMessage"),
      () => {
        if (!props.massMessage) return;
        groupIdsLocal.value.push(...props.massMessage.groupIds);
        excludedGroupIdsLocal.value.push(...props.massMessage.excludedGroupIds);
      },
      { immediate: true }
    );
    return {
      title,
      loading,
      vOnTitle,
      isEditMode,
      isGroupMode,
      groupIdsLocal,
      recipientCount,
      isShowCancelConfirm,
      resetGroupsSelection,
      excludedGroupIdsLocal,
      showRestrictedWordConfirm,
      restrictedWords,
      onSubmit,
      onConfirm,
      onContinue,
    };
  },
});
